/*
 * Cosmora: measure how visitors answer Shopify's cookie banner.
 *
 * Microsoft Clarity (loaded for every visitor by its Shopify app) gets the whole funnel as
 * session tags and custom events, so accept / decline / partial / ignored rates can be read
 * from Clarity's filters:
 *   event consent_banner_shown          banner displayed on this page
 *   event consent_preferences_opened    "Manage preferences" opened
 *   event consent_accept_all | consent_decline_all | consent_partial
 *   tags  consent_choice  = accept_all | decline_all | partial
 *         consent_granted = analytics / marketing / preferences, or none
 *         consent_via     = banner | preferences
 * "Ignored" = sessions with consent_banner_shown and none of the three decision events.
 *
 * Clarity uploads its queue about every 5 seconds and does not flush when the visitor
 * navigates away, so a decision made just before leaving the page can be lost. Every later
 * page load where Shopify already holds a decision therefore re-applies the consent_choice and
 * consent_granted tags (tags are per session, so repeats don't inflate counts). Count
 * decisions as sessions with consent_banner_shown AND a consent_choice tag: returning visitors
 * who decided in an earlier session never see the banner, so they drop out of that filter.
 *
 * GA4 only gets the decision when the visitor granted analytics: the Google & YouTube app does
 * not load gtag at all before consent or after a decline, and this script does not work around
 * that. Event consent_decision, sent to G-93FCPXN8S4 once the app has loaded gtag.
 *
 * Nothing here identifies the visitor, stores anything on the device, or records consent on
 * the visitor's behalf; it only listens to Shopify's banner and Customer Privacy API.
 */
(function () {
  if (window.cosmoraConsentMeasurement) return;
  window.cosmoraConsentMeasurement = true;

  var GA4_ID = 'G-93FCPXN8S4';
  var BANNER_ID = 'shopify-pc__banner';
  var PREFS_ID = 'shopify-pc__prefs';
  var CATEGORIES = ['analytics', 'marketing', 'preferences'];
  var WAIT_MS = 30000;

  var bannerLogged = false;
  var prefsLogged = false;
  var prefsOpen = false;
  var decisionLogged = false;
  var clarityQueue = [];
  var clarityTimer = null;

  function isShown(element) {
    return !!element && element.isConnected && window.getComputedStyle(element).display !== 'none';
  }

  // Clarity's app may load after this script; hold calls until window.clarity exists.
  function clarity() {
    if (typeof window.clarity === 'function') {
      window.clarity.apply(window, arguments);
      return;
    }
    clarityQueue.push(arguments);
    if (clarityTimer) return;
    var started = Date.now();
    clarityTimer = setInterval(function () {
      if (typeof window.clarity === 'function') {
        clearInterval(clarityTimer);
        clarityQueue.splice(0).forEach(function (args) {
          window.clarity.apply(window, args);
        });
      } else if (Date.now() - started > WAIT_MS) {
        clearInterval(clarityTimer);
        clarityQueue = [];
      }
    }, 250);
  }

  // The Google app loads gtag only after analytics consent, a moment after the decision.
  function whenGtag(callback) {
    var started = Date.now();
    (function check() {
      if (typeof window.gtag === 'function') return callback();
      if (Date.now() - started < WAIT_MS) setTimeout(check, 250);
    })();
  }

  function checkBanner() {
    var banner = document.getElementById(BANNER_ID);
    var prefs = document.getElementById(PREFS_ID);
    watch(banner);
    watch(prefs);

    if (!bannerLogged && !decisionLogged && isShown(banner)) {
      bannerLogged = true;
      clarity('set', 'consent_banner', 'shown');
      clarity('event', 'consent_banner_shown');
    }

    prefsOpen = isShown(prefs);
    if (prefsOpen && !prefsLogged && !decisionLogged) {
      prefsLogged = true;
      clarity('event', 'consent_preferences_opened');
    }
  }

  var styleObserver = new MutationObserver(checkBanner);
  function watch(element) {
    if (!element || element.cosmoraConsentMeasured) return;
    element.cosmoraConsentMeasured = true;
    styleObserver.observe(element, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
  }

  function classify(granted) {
    return granted.length === CATEGORIES.length ? 'accept_all' : granted.length === 0 ? 'decline_all' : 'partial';
  }

  function tagChoice(choice, granted) {
    clarity('set', 'consent_choice', choice);
    clarity('set', 'consent_granted', granted.length ? granted : ['none']);
  }

  // A decision already recorded by Shopify (earlier page or earlier session): tags only, no event.
  function tagStoredDecision() {
    if (decisionLogged) return;
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return;
    var consent = privacy.currentVisitorConsent();
    var answered = CATEGORIES.filter(function (category) {
      return consent[category] === 'yes' || consent[category] === 'no';
    });
    if (answered.length === 0) return;
    var granted = CATEGORIES.filter(function (category) {
      return consent[category] === 'yes';
    });
    tagChoice(classify(granted), granted);
  }

  function onDecision(event) {
    if (decisionLogged) return;
    decisionLogged = true;

    var detail = event.detail || {};
    var granted = CATEGORIES.filter(function (category) {
      return detail[category + 'Allowed'] === true;
    });
    var choice = classify(granted);
    var via = prefsOpen || prefsLogged ? 'preferences' : 'banner';

    // Clarity first and synchronously: it keeps running whatever the answer.
    tagChoice(choice, granted);
    clarity('set', 'consent_via', via);
    clarity('event', 'consent_' + choice);

    if (detail.analyticsAllowed !== true) return;
    whenGtag(function () {
      window.gtag('event', 'consent_decision', {
        send_to: GA4_ID,
        consent_choice: choice,
        consent_analytics: 'granted',
        consent_marketing: detail.marketingAllowed === true ? 'granted' : 'denied',
        consent_preferences: detail.preferencesAllowed === true ? 'granted' : 'denied',
        consent_via: via,
      });
    });
  }

  function start() {
    document.addEventListener('visitorConsentCollected', onDecision);
    new MutationObserver(checkBanner).observe(document.body, { childList: true });
    checkBanner();

    if (window.Shopify && window.Shopify.customerPrivacy) {
      tagStoredDecision();
    } else {
      document.addEventListener('consentTrackingApiLoaded', tagStoredDecision, { once: true });
    }
  }

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
